import * as ko from 'knockout';
import page from 'page';

import { RequestOptions, contentRangeTotal } from '../api/request';
import * as tableDefaultsApi from '../api/table_defaults';
import { session } from '../session';
import { updateLocationWithQueryString } from '../utils';
import { GridChoice, GridChoices, NoChoices } from './grid_choices';
import { GridHostView, IslandGridQuery } from '@/islands/props';
import {
  GridViewDefault,
  firstVisitGridView,
  gridViewDefaultFrom,
  mergeGridView,
} from '@/lib/gridViewMerge';
import { parseJson } from '@/lib/json';
import {
  GridViewState,
  carriesGridView,
  gridViewParams,
  readGridViewState,
  readStoredGridView,
  storedGridView,
} from '@/lib/gridViewState';

/**
 * Where a React grid on a Knockout screen remembers itself, and how it is fed.
 *
 * Two separate things are remembered, deliberately:
 *
 *  - **the view** — which rows, i.e. search, filter and sort. Mirrored into the query string, so a
 *    link reproduces it, and the query string wins on load.
 *  - **the table state** — how it looks: column order, widths, visibility, pinning, density, page
 *    size. An opaque snapshot the table produces and reads back, never in a link.
 *
 * Both are merged with the tenant's default for the table on load, against the default they were
 * last merged with (the **base**, stored beside them): what the user never changed follows the
 * default, what they changed stays theirs.
 */

export type { GridViewState };

export interface GridPage<TRow> {
  rows: TRow[];
  rowCount: number;
}

/** The newest page's raw data, for what a screen shows beside the rows. */
export interface LatestGridPage<TData, TModel> {
  query: IslandGridQuery;
  data: TData[];
  models: TModel[];
  /** What `Content-Range` reported, or `null` when the response carried none. */
  total: number | null;
}

export interface GridPageHandlers<TData, TModel, TRow> {
  instantiate: (data: TData) => TModel;
  toRow: (model: TModel, data: TData) => TRow;
  /**
   * Runs for the newest request only: an earlier page landing late must not describe what is on
   * screen. The search the rows answer is already set when it runs.
   */
  onLatest?: (page: LatestGridPage<TData, TModel>) => void;
}

export type GridPageParams<TSearchParam extends string> = {
  offset: number;
  limit: number;
  filter: string;
  sort: string[];
} & { [P in TSearchParam]: string };

/** `searchParam` is the name the endpoint reads the search box by, which differs per endpoint. */
export type GridQueryParams<TSearchParam extends string> = { filter: string } & {
  [P in TSearchParam]: string;
};

export function pageParams<TSearchParam extends string>(
  query: IslandGridQuery,
  searchParam: TSearchParam
): GridPageParams<TSearchParam> {
  return {
    offset: query.offset,
    limit: query.limit,
    filter: query.filter,
    sort: query.sort,
    [searchParam]: query.search,
  } as GridPageParams<TSearchParam>;
}

/**
 * After a row changed outside the list: the table's rows are server-side, so it refetches; the
 * legacy list refreshes in place.
 */
export function refreshRows(
  tableShown: boolean,
  view: { reload: () => void },
  legacyList: { refresh: () => void } | undefined
): void {
  if (tableShown) {
    view.reload();
  } else {
    legacyList?.refresh();
  }
}

/**
 * Appends the url without query params and the signed-in user to a storage key, so the same key in
 * different pages or tenants does not collide, and users taking turns on one browser each keep
 * their own view.
 */
export function gridStorageKey(key: string): string {
  return `${location.toString().replace(location.search, '')}${key}:${session.getUid()}`;
}

export class GridView<
  TChoices extends Record<keyof TChoices, GridChoice[]> = NoChoices,
> implements GridHostView<TChoices> {
  readonly choices: GridChoices<TChoices>;

  /** Null until the table's first query; work outside the table should use `currentQuery()`. */
  lastQuery: IslandGridQuery | null = null;

  /** Bumping it makes the table refetch the page it is on. */
  reloadToken = ko.observable(0);

  /** The search the rows on screen answer, which the table highlights. */
  search = ko.observable('');

  /** Set once `load` has resolved the view, which the table must not mount before. */
  ready = ko.observable(false);

  /** Set by `load`; adjustable until the table mounts. */
  initialFilter = '';
  initialSort: string[] = [];
  /** The screen's own view for a first visit, set by `load`: what a reset falls back to. */
  private firstVisit: GridViewDefault = firstVisitGridView({});
  /** How the table looked when it was last left, set by `load`: '' for a first visit. */
  storedTableState = '';

  /** The tenant's default for this table, set by `load`: `null` when there is none. */
  tableDefault = ko.observable<unknown>(null);
  /** Set by `load`: the default the stored state was last merged against. */
  tableDefaultBase: unknown = null;
  readonly canEditTableDefault = session.isAdmin();

  private readonly name: string;
  private readonly viewKey: string;
  private readonly tableStateKey: string;
  private readonly baseKey: string;
  private latestPage = 0;

  /**
   * `name` names the grid: its storage keys, which also carry the page url, and its tenant
   * default, which does not — so two tables on different pages need different names. It is the
   * default's key on the server too (`[a-z0-9:-]`, at most 64), so renaming it orphans the
   * tenant's default; the storage formats are versioned by the key suffixes instead.
   */
  constructor(
    name: string,
    ...[choices]: NoChoices extends TChoices
      ? [choices?: GridChoices<TChoices>]
      : [choices: GridChoices<TChoices>]
  ) {
    // Safe: reachable only when `TChoices` is `NoChoices`.
    this.choices = choices ?? new GridChoices<TChoices>({}, () => ({}) as TChoices);
    this.name = name;
    this.viewKey = gridStorageKey(`${name}-view-v3`);
    this.tableStateKey = gridStorageKey(`${name}-table-state-v1`);
    this.baseKey = gridStorageKey(`${name}-table-base-v1`);
  }

  /**
   * The query string if it carries a view, taken as it is. Otherwise the stored view merged with
   * the tenant default — or, with neither a default nor a stored view, `firstVisit`: what the screen
   * opens with by default, such as archived rows hidden. Removing a default condition is a change
   * the view then remembers.
   */
  private read(urlParams: unknown, firstVisit: GridViewDefault): GridViewState {
    if (carriesGridView(urlParams)) {
      return readGridViewState(urlParams);
    }

    const base = gridViewDefaultFrom(this.tableDefaultBase) ?? firstVisit;
    const target = gridViewDefaultFrom(this.tableDefault()) ?? firstVisit;
    return mergeGridView(this.storedView(), base, target);
  }

  /**
   * Resolves once the tenant default is fetched and the restored filter's labels are in.
   */
  async load(urlParams: unknown, options: { firstVisit?: Partial<GridViewState> } = {}): Promise<void> {
    const tenantDefault = await tableDefaultsApi.get(this.name).catch((): null => null);
    this.tableDefault(tenantDefault?.state ?? null);
    this.tableDefaultBase = parseJson(localStorage.getItem(this.baseKey)) ?? null;

    this.firstVisit = firstVisitGridView(options.firstVisit ?? {});
    const view = this.read(urlParams, this.firstVisit);
    this.initialFilter = view.filter;
    this.initialSort = view.sort;
    this.storedTableState = this.tableState();
    if (view.search) {
      this.search(view.search);
    }
    this.ready(true);

    await this.choices.seed(this.initialFilter);
  }

  /**
   * The rows the table is showing, for work outside it: its latest query, or before the first one
   * the view it opens on — not an empty filter, which would reach every row.
   */
  currentQuery(): GridViewState {
    if (this.lastQuery) {
      return { search: this.lastQuery.search, filter: this.lastQuery.filter, sort: this.lastQuery.sort };
    }
    return { search: this.search(), filter: this.initialFilter, sort: this.initialSort };
  }

  /** `currentQuery()` in an endpoint's params, the search sent as `searchParam`. */
  queryParams<TSearchParam extends string>(searchParam: TSearchParam): GridQueryParams<TSearchParam> {
    const query = this.currentQuery();
    return { filter: query.filter, [searchParam]: query.search } as GridQueryParams<TSearchParam>;
  }

  save(state: GridViewState): void {
    updateLocationWithQueryString(gridViewParams(state));
    localStorage.setItem(this.viewKey, storedGridView(state));
  }

  /** '' for a first visit. The table ignores anything it cannot read. */
  private tableState(): string {
    return localStorage.getItem(this.tableStateKey) || '';
  }

  fetchPage<TData, TModel, TRow>(
    query: IslandGridQuery,
    request: (options: RequestOptions) => Promise<TData[]>,
    handlers: GridPageHandlers<TData, TModel, TRow>
  ): Promise<GridPage<TRow>> {
    let total: number | null = null;
    const ticket = ++this.latestPage;

    return request({
      injectTenant: true,
      onHeadersReceived: (headers) => {
        total = contentRangeTotal(headers);
      },
    }).then((data) => {
      const models = data.map((item) => handlers.instantiate(item));
      const rows = models.map((model, index) => handlers.toRow(model, data[index]));

      if (ticket === this.latestPage) {
        this.search(query.search);
        handlers.onLatest?.({ query, data, models, total });
      }

      // Without a total the pager has what is in hand: right for a single page, and honest about
      // not knowing whether there is another.
      return { rows: rows, rowCount: total === null ? rows.length : total };
    });
  }

  /** `fetchPage` for a `makeList` endpoint. */
  fetchListPage<TData, TModel, TRow, TSearchParam extends 'name_prefix' | 'search'>(
    query: IslandGridQuery,
    list: (
      params: GridPageParams<TSearchParam>,
      options: undefined,
      requestOptions: RequestOptions
    ) => Promise<TData[]>,
    searchParam: TSearchParam,
    handlers: GridPageHandlers<TData, TModel, TRow>
  ): Promise<GridPage<TRow>> {
    return this.fetchPage(
      query,
      (requestOptions) => list(pageParams(query, searchParam), undefined, requestOptions),
      handlers
    );
  }

  reload = () => {
    this.reloadToken(this.reloadToken() + 1);
  };

  onNavigate = (url: string) => {
    page(url);
  };

  onQueryChange = (query: IslandGridQuery) => {
    this.lastQuery = query;
    this.save({ search: query.search, filter: query.filter, sort: query.sort });
  };

  /** Opaque here: only the table knows what is in it. */
  onTableStateChange = (state: string) => {
    localStorage.setItem(this.tableStateKey, state);
  };

  /**
   * Stores the opened view now rather than after the first query: the base is replaced here, and
   * if that query failed, the old view would be compared against the new base next time and look
   * like a change the user made, so they would stop following the default.
   */
  onTableOpened = (state: string) => {
    const opened = parseJson(state);
    const view = gridViewDefaultFrom(opened);
    const { quickFilter } = (opened ?? {}) as { quickFilter?: unknown };

    localStorage.setItem(this.tableStateKey, state);
    if (view) {
      const search = typeof quickFilter === 'string' ? quickFilter : '';
      localStorage.setItem(this.viewKey, storedGridView({ search, ...view }));
    }
    this.storeBase(this.tableDefault());
  };

  saveTableDefault = async (state: unknown): Promise<void> => {
    const saved = await tableDefaultsApi.save(this.name, state);

    this.tableDefault(saved.state);
    this.storeBase(saved.state);
  };

  /** The base stays: what users never changed must still be recognised, to fall back to the built-in layout. */
  removeTableDefault = async (): Promise<void> => {
    await tableDefaultsApi.remove(this.name);

    this.tableDefault(null);
  };

  /** The tenant default, or the screen's first-visit view when there is none. */
  resetToTableDefault = async (): Promise<void> => {
    const view = gridViewDefaultFrom(this.tableDefault()) ?? this.firstVisit;

    localStorage.removeItem(this.viewKey);
    localStorage.removeItem(this.tableStateKey);
    this.storeBase(this.tableDefault());
    this.initialFilter = view.filter;
    this.initialSort = view.sort;

    await this.choices.seed(view.filter);
  };

  private storeBase(state: unknown) {
    this.tableDefaultBase = state;
    if (state === null) {
      localStorage.removeItem(this.baseKey);
    } else {
      localStorage.setItem(this.baseKey, JSON.stringify(state));
    }
  }

  /**
   * The view this grid was last left in, or `null` when it never was here.
   *
   * Not `carriesGridView` over the parsed entry: a view whose every condition the user removed is
   * stored as `{}`, which carries no key — counted as "never stored" it would be given the first
   * visit's default filter back on the next visit.
   */
  private storedView(): GridViewState | null {
    return readStoredGridView(localStorage.getItem(this.viewKey));
  }
}
